<template>
  <div>
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push(`/${module}`)"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn  style="float: right"
          class="capitalize ctrl-button right q-ml-md"
          :color="$v.data.$anyError ? 'grey-5' : 'ctrl-turquoise'"
          :disable="$v.data.$anyError"
          :label="`${$store.state.stringsStore.strings.action.save}`"
          @click="saveChanges()"
          icon-right="save"
        />
        <q-btn v-if="data.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete"
          align="right"
          @click="deleteTag()"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">
      <!-- Left -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ 'Tag Details' }}
            </p>

            <!-- Details -->
            <div class="card-block">
              <div class="row">
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('label')"
                  :error="labelSet && !$v.data.label.required"
                  @input="validateLabel"
                  v-model="data.label"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('url')"
                  v-model="data.url"
                />
              </div>
            </div>

            <template v-if="$route.params.id">
              <div class="q-mt-md">
                <p class="card-title">
                  Tag usage
                </p>

                <table-overview
                  :endpoint="`tag/${$route.params.id}/usage`"
                  :responseFormat="'posts'"
                  :columns="columns"
                  permission="tag.view"
                  customEvents
                  @click="goToPost"
                  module="tag"
                  :selectable="false">
                </table-overview>
              </div>
            </template>

          </div>
        </div>
      </div>
      <!-- Right -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pl-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ 'Meta' }}
            </p>

            <div class="card-block">
              <div class="row">
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.seo.title')"
                  v-model="data.meta.seo.title"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.seo.description')"
                  v-model="data.meta.seo.description"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.og.title')"
                  v-model="data.meta.og.title"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.og.description')"
                  v-model="data.meta.og.description"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.og.image')"
                  readonly
                  v-model="ogImage"
                  @click="imageModal = true"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.canonical')"
                  v-model="data.meta.canonical"
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Image select modal -->
    <q-modal no-esc-dismiss v-if="imageModal" v-model="imageModal">
      <div class="row" style="display: flex; justify-content: flex-end">
        <q-btn
          icon="close"
          @click="imageModal = false"
          />
      </div>
      <div style="height: 75vh; width: 80vw;">
        <div class="row q-pa-md">
          <div class="col-7 q-pa-md">
            <library-table :guid="guid" :readonly="true" spacing="45vh"/>
          </div>
          <div class="col-5 q-pa-md">
            <div>
              <image-preview :guid="guid"/>
            </div>
          </div>
        </div>
      </div>
    </q-modal>

  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import EventBus from '@/event-bus';

// Mixins
import fetchDetail from '@/mixins/detailViews/fetchDetail';
import createUuid from '@/helpers/createUuid';
import getString from '@/helpers/GetStrings';
import suggestUrl from '@/mixins/suggestUrl';
import saveChanges from '@/mixins/detailViews/saveChanges';

// Components
import LibraryTable from '@/components/library/LibraryTable.vue';
import ImagePreview from '@/components/library/ImagePreview.vue';
import TableOverview from '@/components/overview/TableOverview.vue';
import common from '../common';

export default {
  mixins: [
    common,
    fetchDetail,
    suggestUrl,
    saveChanges,
  ],
  components: {
    LibraryTable,
    ImagePreview,
    TableOverview,
  },
  validations: {
    data: {
      label: {
        required,
      },
    },
  },
  data() {
    return {
      columns: [
        {
          name: '__slot:nav-title',
          title: getString('title'),
          sortField: 'title',
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m user text-ellipsis',
        },
        {
          name: 'type',
          title: 'Type',
          sortField: 'title',
          callback: val => getString(`module.name.${val}`),
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m user text-ellipsis',
        },
      ],
      guid: createUuid(),
      imageModal: false,
      containsErrors: true,
      data: {
        id: null,
        label: '',
        url: '',
        meta: {
          seo: {
            description: '',
            title: '',
          },
          og: {
            description: '',
            title: '',
            image: '',
          },
          canonical: '',
        },
      },
      labelSet: false,
    };
  },

  methods: {
    getString,
    validateLabel(input) {
      this.suggestUrl(input);
      this.labelSet = true;
      this.$v.$touch();
    },

    // Go to the selected post of the tag
    goToPost(data) {
      if (!data.row.id || !data.row.type) {
        return;
      }
      this.$router.push(`/${data.row.type}/${data.row.id}`);
    },

    deleteTag() {
      // Dialog
      this.$q.dialog({
        title: '',
        message: this.getString('module.library.delete.title.single'),
        ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
        cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
      }).then(() => {
        // delete call
        this.axios.delete(`${this.endpoint}/${this.data.id}`)
          .then(() => {
            // Refresh data to see the result
            this.$q.notify({
              message: this.getString('module.tags.removed'),
              position: 'top-right',
              timeout: 1000,
              color: 'positive',
            });
            this.$router.push('/tags');
          })
          .catch((error) => {
            // Error
            console.warn(error);
          });
      }).catch(() => {
        console.warn('Not removed');
      });
    },

    requestParams() {
      const requiredFields = {
        label: this.data.label,
      };

      if (this.data.url) {
        this.cleanUrl(this.data.url);
      }

      const optional = {
        url: this.data.url,
        meta_seo_title: this.data.meta.seo.title,
        meta_seo_description: this.data.meta.seo.description,
        meta_og_title: this.data.meta.og.title,
        meta_og_description: this.data.meta.og.description,
        meta_og_image: this.data.meta.og.image && this.data.meta.og.image.location && this.data.meta.og.image.location.replace('api/', ''),
        meta_slug: this.data.meta.slug,
        meta_canonical: this.data.meta.canonical,
      };

      // Join required and optional
      const fields = Object.assign(requiredFields, optional);

      // If a value is null, undefined or empty, remove it from fields
      for (const [key, value] of Object.entries(fields)) {
        if (!value) delete fields[key];
      }

      return fields;
    },
  },

  computed: {
    ogImage() {
      return this.data.meta.og.image && this.data.meta.og.image.name;
    },
  },

  mounted() {
    if (this.$route.params.id) {
      this.axios.get(`tag/${this.$route.params.id}`).then((res) => {
        this.data = res.data.tag;
      });
    }

    EventBus.$on(`file-select:${this.guid}`, (data) => {
      this.data.meta.og.image = { location: data.location, name: data.name };
      this.imageModal = false;
    });
  },
};
</script>

<style lang="stylus" scoped>

</style>
